import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { OrganizationData } from '../../../types/types';
import { Header } from '../components/Header';
import { OrganizationProfile } from '../components/OrganizationProfile';
import { InfoCard } from '../components/InfoCard';
import { Container, ContentGrid, MainCard, PageContainer } from '../../../styles/GlobalLayout';
import { Tabs } from '../../../components/Tabs/Tabs';
import cubesBg from '../../../assets/images/cubesBg.png';
import ContactListing from './Contacts/Listing';
import GridStackExample from '../../contacts/ContactOverview/DND/GridStackExample';
import { useGetOrganizationDetailsQuery } from '../../../api/organizationPortalApiSlice';
import { useGetMultipleOrgRecordsQuery, useGetOrganizationsContactsQuery } from '../../../api/organizationApiSlice';
import { authFetch } from '../../../utils/authFetch';
import { SkeletonCell } from '../../../components/SkeletonCell';
import moment from 'moment';
import PendingJoinRequests from './PendingJoinRequests';
import { selectAuth, setAuth } from '../../auth/authSlice';
import Avatar from '../../../components/Avatar/Avatar';
import FormFiller from '../../../components/FormBuilder/CustomForm/FormFiller';
import NoPrimaryOrganizationView from '../../websitePreview/NoPrimaryOrganizationView';

type Organization = {
    id: number;
    signedUrl?: string;
    account?: {
        firstName?: string;
        lastName?: string;
    };
    createdBy?: {
        firstName?: string;
        lastName?: string;
    };
    name?: string;
    website?: string;
    industry?: string;
    employeeRange?: string;
    contacts?: any;
    cfr?: any;
    source: string;
    createdAt?: Date;
};

type ContactsOrganizationDetails = {
    id: number;
    name?: string;
    legacyId?: string;
    createdAt?: string;
    updatedAt?: string;
    signedUrl?: string;
    website?: string;
    email?: string;
    industry?: string;
    numberOfEmployees?: string;
    account?: {
        firstName?: string;
        lastName?: string;
    };
    lastModifiedByAccount?: {
        firstName?: string;
        lastName?: string;
    };
    roles: {
        id: number;
        roleId: number;
        roleName: string;
    }[];
};

type OrganizationContactsPayload = ContactsOrganizationDetails;

const formatPersonName = (person?: { firstName?: string; lastName?: string }) => {
    const firstName = person?.firstName ?? '';
    const lastName = person?.lastName ?? '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'N/A';
};

const formatDate = (value?: string) => {
    if (!value) {
        return 'N/A';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString();
};

const OrganizationDetails: React.FC = () => {
    const dispatch = useDispatch();
    const auth = useSelector(selectAuth);
    const [organizationsData, setOrganizationsData] = useState<any[]>([]);
    const [organizationsLoading, setOrganizationsLoading] = useState(false);
    const [organizationsRefreshToken, setOrganizationsRefreshToken] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showOrganizationFlowModal, setShowOrganizationFlowModal] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchOrganizations = async () => {
            try {
                setOrganizationsLoading(true);
                const apiBase = process.env.REACT_APP_API_URL;

                if (!apiBase) {
                    if (!cancelled) {
                        setOrganizationsData([]);
                    }
                    return;
                }

                const response = await authFetch(`${apiBase}/portal/contacts/my-organizations`);
                const json: any = await response.json();

                if (!cancelled) {
                    const records = Array.isArray(json?.data)
                        ? json.data
                        : Array.isArray(json?.organizations)
                            ? json.organizations
                            : Array.isArray(json)
                                ? json
                                : [];

                    setOrganizationsData(records);
                }
            } catch {
                if (!cancelled) {
                    setOrganizationsData([]);
                }
            } finally {
                if (!cancelled) {
                    setOrganizationsLoading(false);
                }
            }
        };

        void fetchOrganizations();

        return () => {
            cancelled = true;
        };
    }, [organizationsRefreshToken]);

    const organizationOptions = useMemo(() => {
        return Array.isArray(organizationsData) ? organizationsData : [];
    }, [organizationsData]);

    const queryId = Number(searchParams.get('id'));
    const initialOrgId = Number.isFinite(queryId) && queryId > 0
        ? queryId
        : (auth?.organizationId ?? null);

    const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(initialOrgId);

    useEffect(() => {
        if (selectedOrganizationId) {
            return;
        }

        const fallbackId = auth?.organizationId
            ?? (organizationOptions.length > 0 ? Number(organizationOptions[0]?.id) : null);

        if (fallbackId) {
            setSelectedOrganizationId(fallbackId);
        }
    }, [selectedOrganizationId, auth?.organizationId, organizationOptions]);

    useEffect(() => {
        if (!selectedOrganizationId) {
            return;
        }

        const nextParams = new URLSearchParams(searchParams);
        if (nextParams.get('id') !== String(selectedOrganizationId)) {
            nextParams.set('id', String(selectedOrganizationId));
            setSearchParams(nextParams, { replace: true });
        }
    }, [selectedOrganizationId, searchParams, setSearchParams]);

    const handleSwitchOrganization = (organizationId: number) => {
        setSelectedOrganizationId(organizationId);
        setCurrentPage(1);

        const nextAuth = {
            ...auth,
            organizationId,
        };
        dispatch(setAuth(nextAuth as any));
        localStorage.setItem('selectedOrganizationId', String(organizationId));
    };

    const switchOrganizationItems = useMemo(
        () => organizationOptions.map((organization: any) => ({
            label: organization?.name || `Organization ${organization?.id}`,
            id: organization?.id,
            onClick: () => handleSwitchOrganization(Number(organization?.id)),
            selected: Number(organization?.id) === Number(selectedOrganizationId),
            icon: (
                <Avatar
                    name={organization?.name || 'Organization'}
                    imageUrl={organization?.signedUrl || undefined}
                    size={30}
                    border="none"
                />
            ),
        })),
        [organizationOptions, selectedOrganizationId]
    );

    const selectedOrganizationName = useMemo(() => {
        return organizationOptions.find((item: any) => Number(item?.id) === Number(selectedOrganizationId))?.name;
    }, [organizationOptions, selectedOrganizationId]);

    const { data: organizationDetailsData, isLoading, isFetching, refetch } = useGetOrganizationDetailsQuery(
        selectedOrganizationId as number,
        'organization',
        { refetchOnMountOrArgChange: true }
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const canFetchOrganization = Boolean(organizationDetailsData) && !isLoading && !isFetching;
    // const [organization, setOrganization] = useState<Organization>({} as Organization);
    const { data: organization, isLoading: organizationLoading, isFetching: organizationFetching } = useGetMultipleOrgRecordsQuery(selectedOrganizationId as number, { skip: !selectedOrganizationId });
    const [showAddContactForm, setShowAddContactForm] = useState(false);
    const [editingOrgId, setEditingOrgId] = useState<number | null>(null);
    const [editingContactId, setEditingContactId] = useState<number | null>(null);
    // useEffect(() => {
    //     if (!canFetchOrganization || Number.isNaN(id)) {
    //         return;
    //     }

    //     let cancelled = false;

    //     const fetchOrganization = async () => {
    //         try {
    //             const apiBase = process.env.REACT_APP_API_URL;
    //             if (!apiBase) {
    //                 return;
    //             }

    //             const response = await authFetch(`${apiBase}/portal/organizations/multiple?recordIds=${encodeURIComponent(String(id))}`);
    //             const json: any = await response.json();

    //             if (!cancelled) {
    //                 const record = Array.isArray(json) && json.length > 0 ? json[0] : {};
    //                 setOrganization(record as Organization);
    //             }
    //         } catch {
    //             if (!cancelled) {
    //                 setOrganization({} as Organization);
    //             }
    //         }
    //     };

    //     void fetchOrganization();

    //     return () => {
    //         cancelled = true;
    //     };
    // }, [canFetchOrganization, id]);

    const isAdminForSelectedOrganization = useMemo(() => {
        if (!auth?.userId || !Array.isArray(organization) || !selectedOrganizationId) {
            return false;
        }

        const orgRecord =
            organization.find((o: any) => Number(o?.id) === Number(selectedOrganizationId)) ||
            organization[0];

        if (!orgRecord || !Array.isArray(orgRecord.contactOrganizations)) {
            return false;
        }

        const myContactOrg = orgRecord.contactOrganizations.find(
            (entry: any) => Number(entry?.contactId) === Number(auth.userId)
        );

        if (!myContactOrg || !Array.isArray(myContactOrg.contactOrgRoles)) {
            return false;
        }

        const adminRole = myContactOrg.contactOrgRoles.find((roleEntry: any) => {
            const roleName =
                roleEntry?.role?.name || roleEntry?.roleName || '';
            const roleId = roleEntry?.role?.id ?? roleEntry?.roleId;
            return (
                (typeof roleName === 'string' && roleName.toLowerCase() === 'admin') ||
                roleId === 30
            );
        });

        return Boolean(adminRole);
    }, [auth?.userId, organization, selectedOrganizationId]);

    const [tabs, setTabs] = useState([
        { id: 1, title: 'Organization Overview', active: true },
        { id: 2, title: 'Contact Listings', active: false },
        { id: 3, title: 'Pending Requests', active: false },

    ]);

    const [pendingJoinCount, setPendingJoinCount] = useState<number | null>(null);


    const handleChangeTabs = (id: number) => {
        setTabs((prevTabs) =>
            prevTabs.map((tab) => ({
                ...tab,
                active: tab.id === id,
            }))
        );
    };

    const visibleTabs = useMemo(
        () => (isAdminForSelectedOrganization ? tabs : tabs.filter((tab) => tab.id !== 3)),
        [tabs, isAdminForSelectedOrganization]
    );

    const activeTabTitle = useMemo(
        () => visibleTabs.find((tab) => tab.active)?.title ?? 'Organization Overview',
        [visibleTabs]
    );

    const activeTabId = useMemo(
        () => visibleTabs.find((tab) => tab.active)?.id ?? 1,
        [visibleTabs]
    );

    useEffect(() => {
        setTabs((prevTabs) =>
            prevTabs.map((tab) => {
                if (tab.id !== 3) return tab;
                const baseTitle = 'Pending Requests';
                const suffix = pendingJoinCount && pendingJoinCount > 0 ? ` (${pendingJoinCount})` : '';
                return { ...tab, title: `${baseTitle}${suffix}` };
            })
        );
    }, [pendingJoinCount]);

    const { data: organizationContactsData, isLoading: contactsLoading, isFetching: contactsFetching, refetch: refetchContacts } = useGetOrganizationsContactsQuery(
        { page: currentPage, perPage, id: selectedOrganizationId ?? 0 },
        { skip: !selectedOrganizationId, refetchOnMountOrArgChange: true }
    );

    const organizationMeta = (organizationContactsData?.organization as OrganizationContactsPayload | undefined);
    const isProfileLoading = isLoading || isFetching || contactsLoading || contactsFetching || organizationLoading || organizationFetching || organizationsLoading;
    const organizationData: OrganizationData = {
        info: {
            name: organizationMeta?.name || 'N/A',
            email: organizationMeta?.email || 'N/A',
            website: organizationMeta?.website || 'N/A',
            organizationId: String(organizationMeta?.id || 'N/A'),
            logoUrl: organizationMeta?.signedUrl,
        },
        about: {
            industry: organizationMeta?.industry || 'N/A',
            owner: formatPersonName(organizationMeta?.account),
            region: 'N/A',
            employeeRange: organizationMeta?.numberOfEmployees || 'N/A',
        },
        creation: {
            createdBy: formatPersonName(organizationMeta?.account),
            creationDate: organizationMeta?.createdAt ? (moment(organizationMeta.createdAt).isValid() ? moment(organizationMeta.createdAt).format('MMMM DD, YYYY') : 'N/A') : 'N/A',
            lastModifiedBy: formatPersonName(organizationMeta?.lastModifiedByAccount),
            lastModifiedDate: organizationMeta?.updatedAt ? (moment(organizationMeta.updatedAt).isValid() ? moment(organizationMeta.updatedAt).format('MMMM DD, YYYY') : 'N/A') : 'N/A',
        },
    };

    return (
        <Container>
            <PageContainer>
                {showOrganizationFlowModal && (
                    <NoPrimaryOrganizationView
                        fullName={auth?.fullName || [auth?.firstName, auth?.lastName].filter(Boolean).join(' ') || auth?.email || 'Member'}
                        email={auth?.email || undefined}
                        renderInModal
                        onCloseModal={() => setShowOrganizationFlowModal(false)}
                        onSkipToDashboard={() => setShowOrganizationFlowModal(false)}
                        onCompleteProfile={() => setShowOrganizationFlowModal(false)}
                        onEnterOnboardingIntro={() => setShowOrganizationFlowModal(false)}
                        onCreateOrganizationSuccess={() => {
                            setShowOrganizationFlowModal(false);
                            setOrganizationsRefreshToken((prev) => prev + 1);
                        }}
                    />
                )}
                {editingOrgId && (
                    <FormFiller
                        formType="organization"
                        entityId={editingOrgId}
                        onClose={() => setEditingOrgId(null)}
                        onSuccess={() => {
                            setEditingOrgId(null);
                            setOrganizationsRefreshToken((prev) => prev + 1);
                            refetchContacts();
                            refetch();
                        }}
                        allowFormSwitch={false}
                    />
                )}
                {showAddContactForm && (
                    <FormFiller
                        formType="contact"
                        defaultOrganizationId={selectedOrganizationId ?? undefined}
                        defaultOrganizationName={selectedOrganizationName}
                        lockOrganizationSelection={isAdminForSelectedOrganization}
                        onClose={() => setShowAddContactForm(false)}
                        onSuccess={() => {
                            setShowAddContactForm(false);
                            refetchContacts();
                        }}
                        allowFormSwitch={false}
                    />
                )}
                {editingContactId && (
                    <FormFiller
                        formType="contact"
                        entityId={editingContactId}
                        onClose={() => setEditingContactId(null)}
                        onSuccess={() => {
                            setEditingContactId(null);
                            refetchContacts();
                        }}
                        allowFormSwitch={false}
                    />
                )}
                {/* Top Header Section */}
                <Header
                    title="Organization Details"
                    subtitle="Manage your profile and account related settings here"
                    onAdd={() => setShowOrganizationFlowModal(true)}
                    onEdit={() => {
                        if (selectedOrganizationId) {
                            setEditingOrgId(selectedOrganizationId);
                        }
                    }}
                    showAddEditButton
                    btnTitle={"Switch Organization"}
                    dropdownItems={switchOrganizationItems}
                    showId
                    btnTextColor="var(--app-text-color)"
                    btnBackgroundColor="var(--app-primary-color)"
                    btnHoverColor="var(--app-primary-color)"
                    btnIcon={<span style={{ display: 'inline-flex', alignItems: 'center' }}>◉</span>}
                    menuItemHoverColor="rgba(255, 255, 255, 0.12)"
                    selectedItemBackgroundColor="rgba(255, 255, 255, 0.18)"
                    selectedItemBorderColor="var(--app-text-color)"
                    selectedItemTextColor="var(--app-text-color)"
                    selectedItemIconColor="var(--app-text-color)"
                />

                {/* Main White Card */}
                <MainCard style={{
                    backgroundImage: `url(${cubesBg})`,
                    backgroundSize: 'contain',
                    backgroundPosition: '0',
                    borderRadius: 12,
                    backgroundRepeat: 'no-repeat',
                }}>
                    {isProfileLoading ? (
                        <div className='d-flex justify-content-between gap-3'>
                            <div style={{ display: 'flex', gap: '24px', width: 'calc(100% - 306px)' }}>
                                <SkeletonCell height='120px' width='120px' rounded />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                                    <SkeletonCell height='24px' width='55%' />
                                    <SkeletonCell height='18px' width='45%' />
                                    <SkeletonCell height='18px' width='35%' />
                                    <SkeletonCell height='28px' width='70%' />
                                </div>
                            </div>

                            <ContentGrid>
                                <div style={{ background: 'rgba(255, 255, 255, 0.8)', borderRadius: 12, padding: 16 }}>
                                    <SkeletonCell height='18px' width='45%' style={{ marginBottom: 12 }} />
                                    <SkeletonCell height='18px' width='70%' style={{ marginBottom: 10 }} />
                                    <SkeletonCell height='18px' width='65%' style={{ marginBottom: 10 }} />
                                    <SkeletonCell height='18px' width='60%' style={{ marginBottom: 10 }} />
                                    <SkeletonCell height='18px' width='55%' />
                                </div>
                            </ContentGrid>
                        </div>
                    ) : (
                        <div
                            className='d-flex justify-content-between'

                        >
                            <OrganizationProfile data={organizationData.info} showWebsite />

                            {/* Grid for Info Boxes */}
                            <ContentGrid>
                                {/* <InfoCard

                                title="About this Organization"
                                items={[
                                    { label: 'Industry', value: organizationData.about.industry },
                                    { label: 'Owner', value: organizationData.about.owner },
                                    { label: 'Region', value: organizationData.about.region },
                                    { label: 'Employee Range', value: organizationData.about.employeeRange },
                                ]}
                            /> */}

                                <InfoCard
                                    title="Creation Details"
                                    items={[
                                        { label: 'Created By', value: organizationData.creation.createdBy },
                                        { label: 'Creation Date', value: organizationData.creation.creationDate },
                                        { label: 'Last Modified By', value: organizationData.creation.lastModifiedBy },
                                        { label: 'Last Modified Date', value: organizationData.creation.lastModifiedDate },
                                    ]}
                                />
                            </ContentGrid>
                        </div>
                    )}

                    {!isProfileLoading ? <Tabs tabs={visibleTabs} onChangeTabs={handleChangeTabs} /> : null}



                </MainCard>
                {activeTabId === 1 && (
                    isLoading || isFetching ? (
                        <div className='pt-3'>
                            <div style={{ display: 'grid', gap: '16px' }}>
                                <div style={{ background: 'white', borderRadius: 12, padding: 16 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                                        <SkeletonCell height='250px' width='100%' />
                                        <SkeletonCell height='250px' width='100%' />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : <div className='pt-3'>
                        <GridStackExample
                            scope='organization'
                            widgets={organizationDetailsData?.widgets || []}
                            setWidgets={() => undefined}
                            refetch={refetchContacts}
                            widgetRefetch={refetch}
                            userDataRefetch={() => undefined}
                            isLoading={false}
                            backupWidget={undefined}
                            organization={Array.isArray(organization) ? organization[0]?.contactOrganizations : undefined}
                        />
                    </div>
                )}

                {activeTabId === 2 && (
                    <div className='pt-3'>
                        <ContactListing
                            roles={organizationMeta?.roles}
                            organizationContactsData={organizationContactsData}
                            contactsLoading={contactsLoading}
                            contactsFetching={contactsFetching}
                            currentPage={currentPage}
                            perPage={perPage}
                            onPageChange={setCurrentPage}
                            onLimitChange={(nextPerPage) => {
                                setPerPage(nextPerPage);
                                setCurrentPage(1);
                            }}
                            refetchOrgContacts={refetchContacts}
                            onAddContact={() => setShowAddContactForm(true)}
                            onEditContact={(contact) => {
                                if (contact?.id) {
                                    setEditingContactId(contact.id);
                                }
                            }}
                        />
                    </div>
                )}
                {isAdminForSelectedOrganization && activeTabId === 3 && (
                    <PendingJoinRequests
                        organizationId={selectedOrganizationId ? String(selectedOrganizationId) : ''}
                        organizationName={selectedOrganizationName}
                        onPendingCountChange={setPendingJoinCount}
                    />
                )}
            </PageContainer>

        </Container>
    );
};

export default OrganizationDetails;